// Component Name:  InfoBar component
// Author: Lavanya S
// Platform:
// OS: Windows
// IDE: Visual Studio Code
// Reference: Original
// Date: 17-07-2024
// Description:  This is an non-scrollable info bar component, with fixed height
// we can expand and collapse the info bar
// when expanded it act as a buttons
// when collapses it displays texts without button
// Dependencies: React library, DropDown
// Interfaces: props
// Maintenance History: 1.0 First Version

// updated to use DropDown component : 30-01-2025

// Moved inline styles to _infobar.scss for better separation of concerns and maintainability : 23-04-2025
// Extracted the Date Range Picker into a separate component : 23-04-2025
// Reduced code repetition by utilizing a map function. Created an array of objects to streamline and centralize data management : 23-04-2025

import React, { useEffect, useState, useRef } from "react";
import setting from "../../assets/images/infobar/LK_Cheveron_Down.svg";
import LKBellImg from "../../assets/images/LK_Icons/LK_Alarm.svg";
import arrowUp from "../../assets/images/infobar/LK_Cheveron_Up.svg";
import arrowDown from "../../assets/images/infobar/LK_Cheveron_Down.svg";
import LKMenuImg from "../../assets/images/LK_Icons/LK_Menu.svg";
import LKFilterImg from "../../assets/images/LK_Icons/LK_Configurator.svg";
import LKWhiteMenuImg from "../../assets/images/LK_Icons/LK_White_Menu.svg";
import LKWhiteFilterImg from "../../assets/images/LK_Icons/LK_White_Configurator.svg";
import LKWhiteBellImg from "../../assets/images/LK_Icons/LK_White_Alarm.svg";
import arrowUpLight from "../../assets/images/infobar/arrow-up-light.png";
import arrowDownLight from "../../assets/images/infobar/arrow-down-light.png";
import menu1 from "../../assets/images/infobar/menu1.png";
import menu2 from "../../assets/images/infobar/menu2.png";
import expoMart from "../../assets/images/infobar/india_expo_mart.jpg";
import "flatpickr/dist/flatpickr.min.css";
import { connect } from "react-redux";
import { dashboardData, dateRangeData, reportData } from "./dbConstants";
import { MONTHS, REPORT_DETAILS } from "../Utility/Constants";
import DropDown from "../Utility/DropDown";
import DateRangePicker from "../Utility/DateRangePicker";
import { loadLocation } from "../../api/utils/loadLocations";
import useIsMounted from "../Utility/useIsMounted";
import { extractCity } from "../../api/utils/pinCode";
import { updateReportData } from "../../store/dbFilter/actions";

const InfoBar = (props) => {
  const { toggleInfobarCallback, infobarType, transactionData, filterData, updateReportData } = props;
  const { session, units, co2Saving, revenue, locationName } = transactionData;
  const { dateRange, handleDateRange, location, handleLocation, handleResetFilter } = filterData;
  const isMounted = useIsMounted();
  const [isExpand, setIsExpand] = useState(false);
  // to maintain filters dropdown(calendar,date range,clear)
  const [isFilterDropdown, setIsFilterDropdown] = useState(false);
  // to maintain the report dropdown
  const [isMenuDropdown, setIsMenuDropdown] = useState(false);

  // start and end date range
  const [startDate, setStartDate] = useState(dateRange[0]);
  const [endDate, setEndDate] = useState(dateRange[1]);

  // dateString will be displayed on the infobar
  const [dateString, setDateString] = useState();

  const filterDropdownRef = useRef(null);

  const buttonRef = useRef(null);
  const calendarRef = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);

  const [locationData, setLocationData] = useState([]);
  // location options and selected options
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({});
  // console.log("locationOptions: ", locationOptions);

  const [selectedDateRange, setSelectedDateRange] = useState(dateRangeData[6]);

  // UI
  const [selectedButton, setSelectedButton] = useState(null);

  // to show the list of selected filter(calendar,location)
  const [selectedFiltered, setSelectedFiltered] = useState("");

  const [downloadReport, setDownloadReport] = useState("");

  const [cityName, setCityName] = useState("");

  const transactionDetail = [
    { name: "Sessions", value: session },
    { name: "Units", value: units, sub: "kWh" },
    { name: "CO₂ savings", value: co2Saving, sub: "kg" },
    { name: "Revenue", value: `₹ ${revenue}` },
  ];

  const hostDetails = [
    { name: "Client", value: props.name },
    { name: "City", value: cityName },
    { name: "Location", value: locationName },
    { name: "Time Period", value: dateString },
  ];

  // to calc the date string for the Infobar display
  useEffect(() => {
    let dateStr = "";
    switch (selectedDateRange?.value) {
      case "today":
        dateStr = "Today";
        break;
      case "yesterday":
        dateStr = "yesterday";
        break;
      case "day7":
        dateStr = "Last 7 days";
        break;
      case "day30":
        dateStr = "Last 30 days";
        break;
      default:
        const startDate = new Date(dateRange[0]) || "";
        const endDate = new Date(dateRange[1]) || "";

        if (startDate && endDate) {
          const firstDayOfMonth = new Date("2024-01-01");
          const today = new Date();
          firstDayOfMonth.setHours(0, 0, 0, 0);
          today.setHours(23, 59, 59, 999);
          // Format the start and end dates
          const startString = `${MONTHS[startDate?.getMonth()] || ""}-${startDate?.getFullYear() || ""}`;
          const endString = `${MONTHS[endDate?.getMonth()] || ""}-${endDate?.getFullYear() || ""}`;
          if (startDate?.getTime() === firstDayOfMonth?.getTime() && endDate?.getTime() === today?.getTime()) {
            dateStr = `Till Date - ${endString}`;
          } else {
            dateStr = `${startString} to ${endString}`;
          }
        } else {
          dateStr = "Invalid date range";
        }
        break;
    }
    setDateString(dateStr);
  }, [dateRange]);

  // Based on the date range type selected by the user,
  // start date and end date will be calculated
  const calcDateRange = (dateRangeType) => {
    const today = new Date();
    let start = null;
    let end = null;
    setEndDate(null);
    setStartDate(null);

    switch (dateRangeType) {
      case "today":
        start = new Date(today);
        end = new Date(today);
        break;

      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = new Date(yesterday);

        const yesterdayEnd = new Date(yesterday);
        end = yesterdayEnd;
        break;

      case "day7":
        const last7DaysStart = new Date(today);
        last7DaysStart.setDate(today.getDate() - 6);
        start = last7DaysStart;

        end = new Date(today);
        break;

      case "day30":
        const last30DaysStart = new Date(today);
        last30DaysStart.setDate(today.getDate() - 29);
        start = last30DaysStart;

        end = new Date(today);
        break;

      case "thisMonth":
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        start = firstDayOfMonth;

        end = new Date(today);
        break;

      case "lastMonth":
        const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

        start = firstDayOfLastMonth;
        end = lastDayOfLastMonth;
        break;

      default:
        break;
    }

    handleDateRange(start, end);
  };

  // Close dropdown if clicked outside
  const handleClickOutside = (event) => {
    const startInstance = startRef.current ? startRef.current.flatpickr : null;
    const endInstance = endRef.current ? endRef.current.flatpickr : null;

    // Prevent closing dropdown when clicking inside the filter button or its dropdown
    if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
      if (calendarRef?.current) {
        if (
          !calendarRef.current.contains(event.target) &&
          startInstance &&
          startInstance.calendarContainer &&
          !startInstance.calendarContainer?.contains(event.target) &&
          endInstance &&
          endInstance.calendarContainer &&
          !endInstance.calendarContainer?.contains(event.target)
        ) {
          setIsFilterDropdown(false);
          setSelectedButton(null);
          setSelectedFiltered("");
        }
      } else {
        setIsFilterDropdown(false);
        setSelectedFiltered("");
        if (selectedButton === 9) {
          // If filter button is selected, reset the state
          setSelectedButton(null);
        }
        setSelectedButton(null);
      }
    }
  };

  // isExpand is set based on the value from the redux
  useEffect(() => {
    infobarType === "collapsed" ? setIsExpand(false) : setIsExpand(true);
  }, [infobarType]);

  // button selection for UI color change
  const handleClick = (buttonIndex) => {
    isExpand && setSelectedButton(buttonIndex);
  };

  // handle info bar expand and collapse
  const handleExpand = () => {
    setSelectedButton(null);
    toggleInfobarCallback();
  };
  useEffect(() => {
    handleLoadLocations();
  }, [props?.locationIds]);
  // load the images map for each location images
  const handleLoadLocations = async () => {
    try {
      const data = await loadLocation(props.locationIds);
      isMounted.current && setLocationData(data);
    } catch (e) {
      console.log("Error Images Data: ", e);
    }
  };
  // to show the location options list and highlight the selected option
  useEffect(() => {
    const options = locationData?.map((item) => {
      if (item?.id === location && location?.length > 0) {
        setSelectedLocation({ name: item?.name, value: item?.id });
      }
      return { name: item?.name, value: item?.id };
    });

    if (options?.length === 0) {
      setLocationOptions([{ name: "No Location", value: "no Location" }]);
    } else {
      setLocationOptions([{ name: "All", value: "" }, ...options]);
    }
  }, [locationData]);

  // Add event listener for clicks outside on component mount
  useEffect(() => {
    fetchCityDetails();
    handleLoadLocations();

    // Add event listener when the component mounts
    document.addEventListener("click", handleClickOutside);
    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const fetchCityDetails = async () => {
    try {
      const data = await extractCity(props.address);
      // console.log("cityName: ", cityName);
      setCityName(data);
    } catch (e) {
      console.log("error fetch city details: ", e);
    }
  };
  // reset all the options and values to the default values
  const resetFilterOptions = () => {
    handleResetFilter();
    setSelectedDateRange(dateRangeData[6]);
    setSelectedLocation({});
    setSelectedFiltered("");
    setIsFilterDropdown(!isFilterDropdown);
    setSelectedButton(null);
  };

  return (
    <div
      className="d-flex justify-content-center w-100"
      style={{
        gap: isExpand ? "0.25rem" : "0",
      }}
    >
      {isExpand ? (
        //  Expanded Info bar starts here
        <>
          <div className="db-ib-common-Container  db-ib-first-container  justify-content-between">
            <img src={expoMart} alt="img" className="db-ib-img-icon" />
            {/* host detail container for expanded Infobar */}
            {hostDetails?.map((item, index) => {
              return (
                <button
                  className={`db-ib-btn-expanded ${selectedButton === index && "selected"}`}
                  onClick={() => {
                    handleClick(index);
                  }}
                  key={index}
                >
                  <span className="db-ib-client-heading-text">{item?.name}</span>
                  <span className="db-ib-client-info-text">{item?.value}</span>
                </button>
              );
            })}

            <img src={setting} alt="img" style={{ paddingRight: "15px" }} />
          </div>
          {/* arrow: on click collapses the infobar */}
          <div>
            <img src={arrowUp} alt="img" className="db-ib-arrow-icon" onClick={handleExpand} />
          </div>
          <div className="db-ib-common-Container db-ib-middle-container justify-content-around">
            {/* transaction detail container for expanded Infobar */}
            {transactionDetail?.map((item, index) => {
              const itemIndex = index + 4;
              return (
                <button key={index} className={`db-ib-btn-expanded ${selectedButton === itemIndex && "selected"}`} onClick={() => handleClick(itemIndex)}>
                  <span className="db-ib-parameter-heading">{item.name}</span>
                  <span className="db-ib-parameter-info-text">
                    {item.value} {item?.sub && <span style={{ fontSize: "14px" }}>{item?.sub}</span>}
                  </span>
                </button>
              );
            })}

            <button
              ref={buttonRef}
              className={`db-ib-img-btn db-ib-btn-expanded ${selectedButton === 9 && "selected"}`}
              onClick={() => {
                handleClick(9);
                setIsFilterDropdown(!isFilterDropdown);
                setSelectedFiltered("");
              }}
            >
              <img src={selectedButton === 9 ? LKWhiteFilterImg : LKFilterImg} alt="img" style={{ width: "25px", height: "25px" }} />
            </button>
            {/* Filter Dashboard based on selected date range and location */}
            {isFilterDropdown && (
              <>
                <div ref={filterDropdownRef} className="db-ib-filter-dropdown-container">
                  <DropDown
                    options={dashboardData}
                    selectedOption={selectedFiltered}
                    onSelect={(optionSelected) => {
                      if (optionSelected?.value === "clear") {
                        resetFilterOptions();
                      } else {
                        setSelectedFiltered(optionSelected);
                      }
                    }}
                  />
                  {/* calendar list */}
                  {selectedFiltered?.name === "Calendar" && (
                    <div className="db-ib-calendar-container">
                      <DropDown
                        options={dateRangeData}
                        selectedOption={selectedDateRange}
                        onSelect={(optionSelected) => {
                          setSelectedDateRange(optionSelected);
                          optionSelected?.value !== "dateRange" && calcDateRange(optionSelected?.value);
                        }}
                      />
                      {/*custom date range picker   */}
                      {selectedDateRange?.value === "dateRange" && (
                        <div className="db-ib-date-range-picker" ref={calendarRef}>
                          <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            startRef={startRef}
                            endRef={endRef}
                            onChangeStart={(date) => {
                              setStartDate(date);
                              if (endDate && endDate < date) {
                                setEndDate(null); // Reset invalid end date
                              }
                              handleDateRange(date, endDate);
                            }}
                            onChangeEnd={(date) => {
                              setEndDate(date);
                              handleDateRange(startDate, date);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* location list */}
                  {selectedFiltered?.name === "Location" && (
                    <div className="db-ib-location-container ">
                      <DropDown
                        options={locationOptions}
                        selectedOption={selectedLocation}
                        onSelect={(optionSelected) => {
                          setSelectedLocation(optionSelected);
                          handleLocation(optionSelected?.value);
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {/* last container for report and notification */}
          <div className="db-ib-common-Container db-ib-last-container justify-content-around">
            {/* Report download button */}
            <button
              // ref={buttonRef}
              className={`db-ib-img-btn db-ib-btn-expanded ${selectedButton === 8 && "selected"}`}
              onClick={() => {
                handleClick(8);
                setIsMenuDropdown(!isMenuDropdown);
                setDownloadReport("");
              }}
            >
              {/* <i className="ri-menu-line"></i> */}
              <img src={selectedButton === 8 ? LKWhiteMenuImg : LKMenuImg} alt="img" style={{ width: "25px", height: "25px" }} />
            </button>
            {/* list of report for download */}
            {isMenuDropdown && (
              <div className="db-ib-menu-dropdown-container">
                <DropDown
                  options={[...REPORT_DETAILS, { name: "All Reports", value: "", icon: "ri-download-fill" }]}
                  onSelect={(optionSelected) => {
                    setDownloadReport(optionSelected);
                    const reportSelected = optionSelected?.value?.length === 0 ? null : optionSelected;
                    const locationTobeDownloaded = Object.keys(selectedLocation)?.length === 0 ? null : selectedLocation;
                    updateReportData({ report: reportSelected, location: locationTobeDownloaded, dateRange: {...selectedDateRange,dateRange} });
                    props.history.push("/cms-report");
                  }}
                  closeDropDown={() => {
                    setIsMenuDropdown(false);
                    setSelectedButton(null);
                  }}
                />
              </div>
            )}

            <button
              className={`db-ib-img-btn db-ib-btn-expanded ${selectedButton === 10 && "selected"}`}
              onClick={() => {
                handleClick(10);
              }}
            >
              <img src={selectedButton === 10 ? LKWhiteBellImg : LKBellImg} alt="img" style={{ width: "25px", height: "25px" }} />
            </button>
          </div>
        </>
      ) : (
        // Collapsed Info bar starts here
        <>
          {/* host detail container for collapsed Infobar */}
          <div className="db-ib-first-container-collapsed ">
            <span className="db-ib-client-info-text ">Client Info</span>
            {hostDetails?.map((item, index) => (
              <span key={index}>{item?.value}</span>
            ))}
          </div>
          <div>
            <img src={arrowDown} alt="img" className="db-ib-arrow-icon " onClick={handleExpand} />
          </div>
          {/* transaction detail container for collapsed Infobar */}
          <div className="db-ib-second-container-collapsed ">
            {transactionDetail?.map((item, index) => (
              <button className="db-ib-btn" key={index}>
                <span className="db-ib-parameter-heading">{item?.name}</span>
                <span className="db-ib-parameter-info">
                  {item?.value} {item?.sub && <span className="db-ib-sub-value">{item?.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
const mapStatetoProps = (state) => {
  const { name, address } = state?.Login?.user;
  const { infobarType } = state.Layout;
  const { locationIds } = state?.LocationManagement;
  return { infobarType, locationIds, name, address };
};

export default connect(mapStatetoProps, { updateReportData })(InfoBar);
